import { useEffect, useState } from "react";
import { api } from "../api";
import { ErrorState } from "../ui/State";
export function BankExceptions({
  building,
  accountId,
  onReview,
  onDecision,
}: {
  building?: { id: string } | undefined;
  accountId: string;
  onReview: (id: string) => void;
  onDecision?:
    | ((
        id: string,
        decision: string,
        transactionId?: string,
      ) => Promise<unknown>)
    | undefined;
}) {
  const [page, setPage] = useState(1),
    [data, setData] = useState<any>(),
    [error, setError] = useState<unknown>();
  useEffect(() => {
    setPage(1);
  }, [building?.id, accountId]);
  useEffect(() => {
    const c = new AbortController();
    setData(undefined);
    setError(undefined);
    const q = new URLSearchParams({
      page: String(page),
      ...(building ? { building: building.id } : {}),
      ...(accountId ? { accountId } : {}),
    });
    void api<any>("/api/finance/bank-control/exceptions?" + q, {
      signal: c.signal,
    })
      .then((r) => {
        if (!c.signal.aborted) setData(r);
      })
      .catch((e) => {
        if (!c.signal.aborted) setError(e);
      });
    return () => c.abort();
  }, [building?.id, accountId, page]);
  return (
    <section className="panel">
      <h2>Bank exceptions</h2>
      <p>
        Unallocated transactions, source duplicates, statement differences and
        gaps, feed reviews and account configuration. Items can describe the
        same underlying issue and remain until resolved in their source
        records.
      </p>
      {Boolean(error) && <ErrorState error={error} />}
      <p>
        {data
          ? `${data.total} exception items · page ${page}`
          : "Loading exceptions…"}
      </p>
      {data?.rows.map((r: any) => (
        <article className="summary-row" key={r.id}>
          <div>
            <strong>
              {r.account_name}: {r.kind}
            </strong>
            <p>
              {r.on_date} {r.amount === null ? "" : r.currency + " " + r.amount}{" "}
              · {r.detail}
            </p>
            {r.kind === "Feed source review" && onDecision && (
              <FeedReview
                id={r.id.slice(5)}
                accountId={r.bank_account_id}
                building={building}
                onDecision={onDecision}
              />
            )}{" "}
            {r.transaction_id && (
              <button onClick={() => onReview(r.transaction_id)}>
                Review transaction
              </button>
            )}
          </div>
        </article>
      ))}
      <div className="actions">
        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Previous exceptions
        </button>
        <button disabled={!data?.hasMore} onClick={() => setPage((p) => p + 1)}>
          Next exceptions
        </button>
      </div>
    </section>
  );
}

function FeedReview({
  id,
  accountId,
  building,
  onDecision,
}: {
  id: string;
  accountId: string;
  building?: { id: string } | undefined;
  onDecision: (
    id: string,
    decision: string,
    transactionId?: string,
  ) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false),
    [source, setSource] = useState<any>(),
    [transactions, setTransactions] = useState<any>(),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState(""),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const c = new AbortController(),
      scope = building ? "&building=" + building.id : "";
    void Promise.all([
      api<any>("/api/finance/bank-control/feed-lines/" + id + "?" + scope, {
        signal: c.signal,
      }),
      api<any>(
        `/api/finance/bank-control/transactions?accountId=${accountId}&page=${page}${scope}`,
        { signal: c.signal },
      ),
    ])
      .then(([s, t]) => {
        if (!c.signal.aborted) {
          setSource(s);
          setTransactions(t);
        }
      })
      .catch((e) => {
        if (!c.signal.aborted) setError(e);
      });
    return () => c.abort();
  }, [open, id, accountId, page, building?.id]);
  const decide = async (d: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await onDecision(id, d, d === "Duplicate" ? selected : undefined);
    } finally {
      setBusy(false);
    }
  };
  if(source?.provider==='FreeAgent')return <p><a href="/admin/freeagent">Review this source in FreeAgent bank connections</a></p>;
  return (
    <details open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Review feed source</summary>
      {Boolean(error) && <ErrorState error={error} />}
      <p>
        {source?.source?.timestamp} · {source?.source?.currency}{" "}
        {source?.source?.amount} · {source?.source?.description}
      </p>
      <p>{source?.reason}</p>
      <p>
        Use the bank review reason above. Choose an existing transaction only
        after checking the retained source; otherwise confirm a distinct
        transaction.
      </p>
      <label>
        Existing transaction
        <select value={selected} onChange={(e) => setSelected(e.target.value)}>
          <option value="">Choose an existing transaction</option>
          {transactions?.rows.map((t: any) => (
            <option key={t.id} value={t.id}>
              {t.transaction_date} · {t.currency} {t.amount} · {t.description}
            </option>
          ))}
        </select>
      </label>
      <div className="actions">
        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Previous candidates
        </button>
        <button
          disabled={!transactions?.hasMore}
          onClick={() => setPage((p) => p + 1)}
        >
          Next candidates
        </button>
        <button
          disabled={busy || !selected}
          onClick={() => void decide("Duplicate")}
        >
          Confirm existing transaction
        </button>
        <button disabled={busy} onClick={() => void decide("Distinct")}>
          Confirm distinct transaction
        </button>
      </div>
    </details>
  );
}
